/**
 * NOVA CHARTER — Gamepad API source.
 *
 * What this module does
 *  - Scans `navigator.getGamepads()` once per animation frame and turns raw pad
 *    state into normalised edge events (`down` / `up` / `repeat`) for the
 *    `InputButton` vocabulary in `src/game/types.ts`.
 *  - Absorbs the ugly parts of the Gamepad API: non-standard mappings, d-pads
 *    that are really HID hat switches on an axis, triggers that live on buttons
 *    on one browser and on axes on another, and browsers that only reveal pads
 *    after the first press.
 *  - Keeps a per-pad analog snapshot (deadzoned) for smooth camera / aim work.
 *
 * What it deliberately does NOT do: seats. Slot numbers here are always
 * `Gamepad.index`; `inputRouter.ts` maps them onto `PlayerId`s.
 *
 * Hot-path rule: `poll()` must not allocate. All per-pad state objects are
 * created once on connect and mutated in place, all loops are indexed (an
 * array `for..of` allocates an iterator), and the only object the manager ever
 * hands out is its own reused axis snapshot.
 */

import type { InputButton, InputEvent } from '../game/types';
import {
  REPEAT_DELAY_MS,
  REPEAT_INTERVAL_MS,
  STICK_DEADZONE,
  TRIGGER_THRESHOLD,
} from '../game/constants';

type Edge = InputEvent['edge'];

/* ------------------------------------------------------------------- public */

/**
 * Mutable analog snapshot for one pad. Structurally identical to `SeatAxes`
 * in `inputRouter.ts` (kept structural so the two modules stay independent).
 */
export interface AnalogAxes {
  lx: number;
  ly: number;
  rx: number;
  ry: number;
  lt: number;
  rt: number;
}

/**
 * Emitted for every button edge. Positional arguments rather than an event
 * object so the hot path allocates nothing at all — the router builds the one
 * `InputEvent` object per edge, after it has resolved the seat.
 */
export type GamepadEmit = (
  index: number,
  button: InputButton,
  edge: Edge,
  value: number,
) => void;

export interface GamepadManagerOptions {
  onEvent: GamepadEmit;
  /** Fired when a pad appears, whether via event or via a poll rescan. */
  onConnect?: (index: number, id: string, label: string) => void;
  onDisconnect?: (index: number) => void;
}

/* ----------------------------------------------------------------- tunables */

/** Six hex neighbours → 60° per sector. */
const SECTOR_RAD = Math.PI / 3;

/**
 * Extra angle the stick must travel past a sector boundary before the snapped
 * direction changes. Without it a stick parked on a boundary (very common at
 * 30°/90°/150°) flip-flops between two neighbours every frame.
 */
const SECTOR_HYSTERESIS_RAD = 0.14; // ~8°

/**
 * Trigger release threshold. A single threshold would chatter for anyone
 * resting a finger on the trigger, so releasing needs 0.1 of slack.
 */
const TRIGGER_RELEASE_THRESHOLD = TRIGGER_THRESHOLD - 0.1;

/** Below this a hat axis counts as centred (pads that idle at exactly 0). */
const HAT_DEADBAND = 0.08;
/** Above this a hat axis counts as centred (pads that idle at 3.2857). */
const HAT_CENTRE_LIMIT = 1.05;

/** Digital threshold for a d-pad exposed as a pair of analog axes. */
const DPAD_AXIS_THRESHOLD = 0.5;

/** Tolerance for "the stick is exactly vertical", where two hexes tie. */
const VERTICAL_TIE_EPS = 1e-3;

/** Slots reserved per pad for button state. Standard pads report 17. */
const BUTTON_SLOTS = 20;

/* ------------------------------------------------------- direction plumbing */

const DIR_UP = 0;
const DIR_DOWN = 1;
const DIR_LEFT = 2;
const DIR_RIGHT = 3;
const DIR_COUNT = 4;

const DIR_BUTTON: readonly InputButton[] = ['up', 'down', 'left', 'right'];

/**
 * Left stick → hex direction mapping.
 *
 * The galaxy is a **pointy-top** hex grid, so a hex has six edge neighbours at
 * screen angles 0°, 60°, 120°, 180°, 240°, 300° — East and West are pure
 * cardinals, but there is no neighbour straight up or straight down. The stick
 * angle is snapped to the nearest of those six, then expressed in the four
 * directional buttons the `InputEvent` vocabulary has:
 *
 * | sector | angle | hex neighbour | buttons emitted (dominant axis first) |
 * |--------|-------|---------------|----------------------------------------|
 * | 0      | 0°    | East          | `right`                                |
 * | 1      | 60°   | South-East    | `down` + `right`                       |
 * | 2      | 120°  | South-West    | `down` + `left`                        |
 * | 3      | 180°  | West          | `left`                                 |
 * | 4      | 240°  | North-West    | `up` + `left`                          |
 * | 5      | 300°  | North-East    | `up` + `right`                         |
 *
 * The four diagonals emit **two** events (a two-button combination, exactly as
 * a real d-pad diagonal would), vertical first because for a pointy-top hex
 * the vertical component of a diagonal neighbour is the dominant one
 * (|sin 60°| = 0.87 vs |cos 60°| = 0.5). A consumer that only cares about
 * 4-way movement can therefore obey the first event and ignore the second; a
 * consumer that wants all six can read the held pair. Screen coordinates: `+y`
 * is down, matching the Gamepad API's stick axes.
 */
const SECTOR_DIRS: readonly (readonly number[])[] = [
  [DIR_RIGHT], // 0   East
  [DIR_DOWN, DIR_RIGHT], // 60  South-East
  [DIR_DOWN, DIR_LEFT], // 120 South-West
  [DIR_LEFT], // 180 West
  [DIR_UP, DIR_LEFT], // 240 North-West
  [DIR_UP, DIR_RIGHT], // 300 North-East
];

/**
 * HID hat switch decode table. Eight positions, clockwise from up.
 * @see decodeHatAxis
 */
const HAT_STEP_DIRS: readonly (readonly number[])[] = [
  [DIR_UP],
  [DIR_UP, DIR_RIGHT],
  [DIR_RIGHT],
  [DIR_DOWN, DIR_RIGHT],
  [DIR_DOWN],
  [DIR_DOWN, DIR_LEFT],
  [DIR_LEFT],
  [DIR_UP, DIR_LEFT],
];

/**
 * Scratch buffers. `poll()` is synchronous and never re-entrant, so a couple of
 * module-level scratch objects keep the hot path allocation-free.
 */
const WANTED_DIRS: boolean[] = [false, false, false, false];
const SCRATCH_VEC = { x: 0, y: 0 };

/* ------------------------------------------------------------ pad layouts */

type DpadMode = 'buttons' | 'hat9' | 'axes67' | 'hat6' | 'none';

interface PadLayout {
  /** Button index → logical button. `null` = ignored (or handled elsewhere). */
  readonly buttonMap: readonly (InputButton | null)[];
  /** Button index carrying LT/RT as an analog `value`, or -1. */
  readonly ltButton: number;
  readonly rtButton: number;
  /** Axis carrying LT/RT, or -1. */
  readonly ltAxis: number;
  readonly rtAxis: number;
  readonly dpad: DpadMode;
  /** Index of the "up" d-pad button when `dpad === 'buttons'`. */
  readonly dpadBase: number;
  readonly rxAxis: number;
  readonly ryAxis: number;
}

/**
 * W3C "standard gamepad" button table — what Chrome/Edge report for an Xbox
 * pad, and what Firefox and Safari report for anything they recognise.
 */
const STANDARD_BUTTONS: readonly (InputButton | null)[] = [
  'a', 'b', 'x', 'y', // 0-3   face
  'lb', 'rb', // 4-5   shoulders
  null, null, // 6-7   triggers: analog, see updateTrigger()
  'back', 'start', // 8-9
  null, null, // 10-11 stick clicks: unused by NOVA CHARTER
  'up', 'down', 'left', 'right', // 12-15 d-pad
  null, // 16    guide/home: never consumed, it opens OS overlays
];

/**
 * Fallback table for pads whose triggers sit on axes: the classic Linux `xpad`
 * / generic-HID order that Firefox exposes for unrecognised pads. Back/Start
 * move down to 6/7 and the d-pad, if it exists at all, starts at 11.
 */
const AXIS_TRIGGER_BUTTONS: readonly (InputButton | null)[] = [
  'a', 'b', 'x', 'y', // 0-3
  'lb', 'rb', // 4-5
  'back', 'start', // 6-7
  null, null, null, // 8 guide, 9-10 stick clicks
  'up', 'down', 'left', 'right', // 11-14 d-pad (only if present)
];

/** Xbox pads are the reference controller for NOVA CHARTER; label them nicely. */
const XBOX_ID_HINTS: readonly string[] = ['xbox', 'x-box', '045e', 'xinput'];

/* ------------------------------------------------------------------- state */

interface PadState {
  /** Currently connected. False keeps the object alive as reconnect memory. */
  present: boolean;
  /** Mark-and-sweep flag for poll-based disconnect detection. */
  seen: boolean;
  index: number;
  id: string;
  label: string;
  xbox: boolean;
  layout: PadLayout;
  /** Digital button held state, indexed by raw button index. */
  buttons: boolean[];
  /** `[lt, rt]` hysteresis latch. */
  trigDown: boolean[];
  /** `[lt, rt]` — true once the trigger axis has been seen resting at -1. */
  trigFullRange: boolean[];
  /** Held state / next repeat deadline per direction slot. */
  dirHeld: boolean[];
  dirNext: number[];
  /** Last snapped stick sector, or -1 when centred. */
  sector: number;
  /** Rescaled stick magnitude 0..1, reported as the event `value`. */
  stickMag: number;
  axes: AnalogAxes;
}

/* ---------------------------------------------------------------- helpers */

function clamp01(v: number): number {
  if (!(v > 0)) return 0; // also catches NaN
  return v > 1 ? 1 : v;
}

function zeroAxes(a: AnalogAxes): void {
  a.lx = 0;
  a.ly = 0;
  a.rx = 0;
  a.ry = 0;
  a.lt = 0;
  a.rt = 0;
}

/**
 * Read an axis defensively: indices vary wildly between pads and a missing
 * axis must read as centred rather than `undefined` leaking into the maths.
 */
function readAxis(pad: Gamepad, idx: number): number {
  if (idx < 0 || idx >= pad.axes.length) return 0;
  const v = pad.axes[idx];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Raw analog value of a button, 0 when the button does not exist. */
function readButtonValue(pad: Gamepad, idx: number): number {
  if (idx < 0 || idx >= pad.buttons.length) return 0;
  const b = pad.buttons[idx];
  if (!b) return 0;
  // Some cheap pads report `pressed` but leave `value` at 0 (digital buttons
  // wired into an analog slot), so trust `pressed` as a full deflection.
  if (b.value > 0) return clamp01(b.value);
  return b.pressed ? 1 : 0;
}

function isButtonPressed(pad: Gamepad, idx: number): boolean {
  if (idx < 0 || idx >= pad.buttons.length) return false;
  const b = pad.buttons[idx];
  if (!b) return false;
  return b.pressed || b.value >= 0.5;
}

/**
 * Radial deadzone with rescale, writing into `SCRATCH_VEC`.
 *
 * A per-axis deadzone makes a diagonal push register as a cardinal (the
 * classic square-deadzone bug), so the gate is on vector *length*. The
 * remaining `STICK_DEADZONE..1` band is then rescaled onto `0..1`, otherwise
 * the first countable movement would jump straight to 0.35 and analog camera
 * work would feel notched.
 */
function radialDeadzone(x: number, y: number): void {
  const mag = Math.sqrt(x * x + y * y); // hypot() is markedly slower per frame
  if (!(mag > STICK_DEADZONE)) {
    SCRATCH_VEC.x = 0;
    SCRATCH_VEC.y = 0;
    return;
  }
  const rescaled = Math.min(1, (mag - STICK_DEADZONE) / (1 - STICK_DEADZONE));
  const k = rescaled / mag;
  SCRATCH_VEC.x = x * k;
  SCRATCH_VEC.y = y * k;
}

/** Shortest signed angle from `b` to `a`, in (-π, π]. */
function angleDelta(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/**
 * Decode a HID hat switch reported as a single axis.
 *
 * Firefox (and Chrome for unrecognised pads) exposes an 8-way hat as one axis
 * running from -1 (up) clockwise to +1 (up-left) in steps of 2/7, with the
 * released position *outside* ±1 — many pads report 3.2857. Others idle at
 * exactly 0, which is why values near zero also count as centred (the nearest
 * real position, "down", is 0.143 away).
 */
function decodeHatAxis(v: number, out: boolean[]): boolean {
  if (!(Math.abs(v) <= HAT_CENTRE_LIMIT)) return false; // also rejects NaN
  if (Math.abs(v) < HAT_DEADBAND) return false;
  let step = Math.round((v + 1) * 3.5);
  if (step < 0) step = 0;
  else if (step > 7) step = 7;
  const dirs = HAT_STEP_DIRS[step];
  for (let i = 0; i < dirs.length; i++) out[dirs[i]] = true;
  return true;
}

/** True if the pad id looks like an Xbox / XInput device. */
export function isXboxPadId(id: string): boolean {
  const low = (id || '').toLowerCase();
  for (let i = 0; i < XBOX_ID_HINTS.length; i++) {
    if (low.indexOf(XBOX_ID_HINTS[i]) !== -1) return true;
  }
  return false;
}

/**
 * Human-readable pad name for the lobby.
 *
 * Chrome appends a parenthetical: `"Xbox Wireless Controller (STANDARD GAMEPAD
 * Vendor: 045e Product: 0b13)"`. Firefox prefixes vendor-product instead:
 * `"045e-0b13-Xbox Wireless Controller"`. Strip both.
 */
export function labelForGamepadId(id: string, index: number): string {
  let s = (id || '').replace(/\s*\([^()]*\)\s*$/, '');
  s = s.replace(/^[0-9a-f]{4}-[0-9a-f]{4}-/i, '').trim();
  if (!s) s = isXboxPadId(id) ? 'Xbox Controller' : `Gamepad ${index + 1}`;
  return s;
}

/**
 * Work out where this pad keeps its buttons, triggers, d-pad and right stick.
 * Called once per connect, never per frame.
 */
function resolveLayout(pad: Gamepad): PadLayout {
  if (pad.mapping === 'standard') {
    return {
      buttonMap: STANDARD_BUTTONS,
      ltButton: 6,
      rtButton: 7,
      // No axis fallback here: under standard mapping axes 2/3 are the *right
      // stick*, so reading axis 2 as LT would make aiming pull the trigger.
      ltAxis: -1,
      rtAxis: -1,
      dpad: resolveDpad(pad, 12),
      dpadBase: 12,
      rxAxis: 2,
      ryAxis: 3,
    };
  }

  // Non-standard. The first question is where the triggers are, because that
  // also decides whether buttons 6/7 are Back/Start or LT/RT. The `xpad`
  // profile is unmistakable: 11 buttons and 8 axes (LT on 2, RT on 5, hat on
  // 6/7). Anything with more buttons is treated as standard-shaped instead.
  const axisTriggers = pad.axes.length >= 6 && pad.buttons.length <= 11;
  if (axisTriggers) {
    return {
      buttonMap: AXIS_TRIGGER_BUTTONS,
      ltButton: -1,
      rtButton: -1,
      ltAxis: 2,
      rtAxis: 5,
      dpad: resolveDpad(pad, 11),
      dpadBase: 11,
      rxAxis: 3,
      ryAxis: 4,
    };
  }
  return {
    buttonMap: STANDARD_BUTTONS,
    ltButton: 6,
    rtButton: 7,
    ltAxis: -1,
    rtAxis: -1,
    dpad: resolveDpad(pad, 12),
    dpadBase: 12,
    rxAxis: 2,
    ryAxis: 3,
  };
}

/**
 * Pick a d-pad source. Real d-pad buttons win; otherwise fall back to the hat
 * axis (9 is the usual home for a HID hat, 6 for a horizontal-only one) or to
 * a pair of digital-ish axes at 6/7.
 */
function resolveDpad(pad: Gamepad, dpadBase: number): DpadMode {
  if (pad.buttons.length > dpadBase + 3) return 'buttons';
  if (pad.axes.length >= 10) return 'hat9';
  if (pad.axes.length >= 8) return 'axes67';
  if (pad.axes.length >= 7) return 'hat6';
  return 'none';
}

/**
 * Read the browser's pad list.
 *
 * Never cache the array: Chrome hands back a fresh snapshot on every call
 * (a stale array simply stops updating), and Chrome/Safari also hide pads
 * entirely until the page has seen a user gesture, so a pad can appear on any
 * later poll without a `gamepadconnected` event ever firing.
 */
function getPads(): (Gamepad | null)[] | null {
  if (typeof navigator === 'undefined') return null;
  if (typeof navigator.getGamepads !== 'function') return null; // pre-2018 Safari
  return navigator.getGamepads();
}

/** Minimal shape of the haptics API, which is not in every TS DOM lib yet. */
interface HapticPad {
  vibrationActuator?: {
    playEffect?: (type: string, params: Record<string, number>) => unknown;
    /** Firefox's older, simpler API. */
    pulse?: (value: number, duration: number) => unknown;
  } | null;
}

/* ---------------------------------------------------------------- manager */

export class GamepadManager {
  private readonly opts: GamepadManagerOptions;
  /** Sparse, indexed by `Gamepad.index`. Entries persist across disconnects. */
  private readonly states: PadState[] = [];
  private running = false;
  private connectedCount = 0;

  // Bound once so add/removeEventListener always see the same reference.
  private readonly onConnected = (e: Event): void => {
    const pad = (e as GamepadEvent).gamepad;
    if (pad) this.ensureState(pad);
  };

  private readonly onDisconnected = (e: Event): void => {
    const pad = (e as GamepadEvent).gamepad;
    if (!pad) return;
    const st = this.states[pad.index];
    if (st && st.present) this.markGone(st);
  };

  constructor(opts: GamepadManagerOptions) {
    this.opts = opts;
  }

  /** Connected pad count. */
  get count(): number {
    return this.connectedCount;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    if (typeof window !== 'undefined') {
      window.addEventListener('gamepadconnected', this.onConnected);
      window.addEventListener('gamepaddisconnected', this.onDisconnected);
    }
    // Pads plugged in before this listener existed are only visible via a scan.
    this.rescan();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('gamepadconnected', this.onConnected);
      window.removeEventListener('gamepaddisconnected', this.onDisconnected);
    }
    this.releaseAll();
  }

  dispose(): void {
    this.stop();
    this.states.length = 0;
    this.connectedCount = 0;
  }

  /** Call once per animation frame. Allocation-free. */
  poll(nowMs: number): void {
    const states = this.states;
    for (let i = 0; i < states.length; i++) {
      const st = states[i];
      if (st) st.seen = false;
    }

    const pads = getPads();
    if (pads) {
      for (let i = 0; i < pads.length; i++) {
        const pad = pads[i];
        // Firefox leaves holes in the array and keeps stale entries with
        // `connected === false`; both must be skipped.
        if (!pad || !pad.connected) continue;
        const st = this.ensureState(pad);
        st.seen = true;
        this.updatePad(pad, st, nowMs);
      }
    }

    // Sweep: Safari (and Chrome across a USB reset) can drop a pad without ever
    // firing `gamepaddisconnected`.
    for (let i = 0; i < states.length; i++) {
      const st = states[i];
      if (st && st.present && !st.seen) this.markGone(st);
    }
  }

  isConnected(index: number): boolean {
    const st = this.states[index];
    return !!st && st.present;
  }

  /** Raw `Gamepad.id`, remembered across disconnects for seat reclaiming. */
  idOf(index: number): string {
    const st = this.states[index];
    return st ? st.id : '';
  }

  label(index: number): string {
    const st = this.states[index];
    return st ? st.label : `Gamepad ${index + 1}`;
  }

  isXbox(index: number): boolean {
    const st = this.states[index];
    return !!st && st.xbox;
  }

  /** Copy the pad's deadzoned analog snapshot into `out`. */
  writeAxes(index: number, out: AnalogAxes): boolean {
    const st = this.states[index];
    if (!st || !st.present) {
      zeroAxes(out);
      return false;
    }
    const a = st.axes;
    out.lx = a.lx;
    out.ly = a.ly;
    out.rx = a.rx;
    out.ry = a.ry;
    out.lt = a.lt;
    out.rt = a.rt;
    return true;
  }

  /** Short haptic pulse. Silently does nothing where haptics are unsupported. */
  rumble(index: number, strength = 0.6, durationMs = 180): void {
    if (!this.isConnected(index)) return;
    const pad = this.livePad(index);
    if (!pad) return;
    const act = (pad as unknown as HapticPad).vibrationActuator;
    if (!act) return; // Safari, and Firefox before 2023
    const s = clamp01(strength);
    const duration = Math.max(1, Math.min(5000, durationMs));
    try {
      if (typeof act.playEffect === 'function') {
        const p = act.playEffect('dual-rumble', {
          startDelay: 0,
          duration,
          weakMagnitude: s,
          strongMagnitude: s,
        });
        // Chrome rejects the promise if the pad vanishes mid-effect or the page
        // loses focus; an unhandled rejection would spam the console.
        swallow(p);
      } else if (typeof act.pulse === 'function') {
        swallow(act.pulse(s, duration));
      }
    } catch {
      /* haptics are strictly cosmetic — never let them break input */
    }
  }

  /**
   * Emit `up` for everything currently held. Used on stop and on disconnect so
   * the game never sees a direction stuck down.
   */
  releaseAll(): void {
    const states = this.states;
    for (let i = 0; i < states.length; i++) {
      const st = states[i];
      if (st) this.releasePad(st);
    }
  }

  /* ---------------------------------------------------------- internals */

  private rescan(): void {
    const pads = getPads();
    if (!pads) return;
    for (let i = 0; i < pads.length; i++) {
      const pad = pads[i];
      if (pad && pad.connected) this.ensureState(pad);
    }
  }

  private livePad(index: number): Gamepad | null {
    const pads = getPads();
    if (!pads) return null;
    for (let i = 0; i < pads.length; i++) {
      const pad = pads[i];
      if (pad && pad.connected && pad.index === index) return pad;
    }
    return null;
  }

  /** Get (or create) the state for a pad, firing `onConnect` on first sight. */
  private ensureState(pad: Gamepad): PadState {
    let st = this.states[pad.index];
    if (!st) {
      st = createPadState(pad);
      this.states[pad.index] = st;
    }
    // A different physical pad may reuse a freed index; re-derive everything.
    if (!st.present || st.id !== pad.id) {
      st.id = pad.id;
      st.label = labelForGamepadId(pad.id, pad.index);
      st.xbox = isXboxPadId(pad.id);
      st.layout = resolveLayout(pad);
      resetPadState(st);
    }
    if (!st.present) {
      st.present = true;
      this.connectedCount++;
      this.opts.onConnect?.(st.index, st.id, st.label);
    }
    return st;
  }

  private markGone(st: PadState): void {
    this.releasePad(st);
    st.present = false;
    st.seen = false;
    if (this.connectedCount > 0) this.connectedCount--;
    zeroAxes(st.axes);
    this.opts.onDisconnect?.(st.index);
  }

  private releasePad(st: PadState): void {
    const map = st.layout.buttonMap;
    for (let i = 0; i < st.buttons.length; i++) {
      if (!st.buttons[i]) continue;
      st.buttons[i] = false;
      const button = i < map.length ? map[i] : null;
      if (button) this.opts.onEvent(st.index, button, 'up', 0);
    }
    for (let slot = 0; slot < 2; slot++) {
      if (!st.trigDown[slot]) continue;
      st.trigDown[slot] = false;
      this.opts.onEvent(st.index, slot === 0 ? 'lt' : 'rt', 'up', 0);
    }
    for (let d = 0; d < DIR_COUNT; d++) {
      if (!st.dirHeld[d]) continue;
      st.dirHeld[d] = false;
      this.opts.onEvent(st.index, DIR_BUTTON[d], 'up', 0);
    }
    st.sector = -1;
    st.stickMag = 0;
  }

  private updatePad(pad: Gamepad, st: PadState, now: number): void {
    const layout = st.layout;

    // --- digital buttons -------------------------------------------------
    const map = layout.buttonMap;
    const limit = Math.min(map.length, st.buttons.length);
    for (let i = 0; i < limit; i++) {
      const button = map[i];
      if (button === null) continue;
      // The d-pad is folded into the directional state machine below so that
      // stick and d-pad share one set of repeat timers.
      if (layout.dpad === 'buttons' && i >= layout.dpadBase && i <= layout.dpadBase + 3) {
        continue;
      }
      const pressed = isButtonPressed(pad, i);
      if (pressed === st.buttons[i]) continue;
      st.buttons[i] = pressed;
      this.opts.onEvent(st.index, button, pressed ? 'down' : 'up', pressed ? 1 : 0);
    }

    // --- analog sticks (deadzoned snapshot) ------------------------------
    const ax = st.axes;
    radialDeadzone(readAxis(pad, 0), readAxis(pad, 1));
    ax.lx = SCRATCH_VEC.x;
    ax.ly = SCRATCH_VEC.y;
    radialDeadzone(readAxis(pad, layout.rxAxis), readAxis(pad, layout.ryAxis));
    ax.rx = SCRATCH_VEC.x;
    ax.ry = SCRATCH_VEC.y;

    // --- triggers --------------------------------------------------------
    this.updateTrigger(pad, st, 0, 'lt', layout.ltButton, layout.ltAxis);
    this.updateTrigger(pad, st, 1, 'rt', layout.rtButton, layout.rtAxis);

    // --- directions ------------------------------------------------------
    this.updateDirections(pad, st, now);
  }

  /**
   * Triggers, with hysteresis. Chrome/Edge expose LT/RT as buttons 6/7 with an
   * analog `value`; Firefox on Linux and generic HID pads expose them as axes
   * 2/5 instead, sometimes resting at -1 and travelling to +1 rather than
   * 0..1. The full-range case is detected once (a resting trigger reads -1 on
   * the very first poll) and latched.
   */
  private updateTrigger(
    pad: Gamepad,
    st: PadState,
    slot: number,
    button: InputButton,
    buttonIdx: number,
    axisIdx: number,
  ): void {
    let v = readButtonValue(pad, buttonIdx);
    if (v <= 0 && axisIdx >= 0 && axisIdx < pad.axes.length) {
      const raw = readAxis(pad, axisIdx);
      if (raw < -0.2) st.trigFullRange[slot] = true;
      v = clamp01(st.trigFullRange[slot] ? (raw + 1) / 2 : raw);
    }

    if (slot === 0) st.axes.lt = v;
    else st.axes.rt = v;

    if (!st.trigDown[slot]) {
      if (v >= TRIGGER_THRESHOLD) {
        st.trigDown[slot] = true;
        this.opts.onEvent(st.index, button, 'down', v);
      }
    } else if (v < TRIGGER_RELEASE_THRESHOLD) {
      st.trigDown[slot] = false;
      this.opts.onEvent(st.index, button, 'up', v);
    }
  }

  /**
   * Unify d-pad and left stick into four held directions with shared
   * auto-repeat: down → (REPEAT_DELAY_MS) → repeat → (REPEAT_INTERVAL_MS) →
   * repeat… Only directions repeat; A/B/X/Y/LB/RB/LT/RT/Start/Back never do.
   */
  private updateDirections(pad: Gamepad, st: PadState, now: number): void {
    const wanted = WANTED_DIRS;
    wanted[0] = false;
    wanted[1] = false;
    wanted[2] = false;
    wanted[3] = false;

    const dpadActive = this.readDpad(pad, st, wanted);
    const sector = this.snapStick(st);
    if (sector >= 0) {
      const dirs = SECTOR_DIRS[sector];
      for (let i = 0; i < dirs.length; i++) wanted[dirs[i]] = true;
    }

    // A d-pad is digital, so it always reports a full-strength deflection.
    const value = dpadActive ? 1 : st.stickMag;

    // Releases first: a diagonal→cardinal transition should never look like
    // three directions being held at once.
    for (let d = 0; d < DIR_COUNT; d++) {
      if (!st.dirHeld[d] || wanted[d]) continue;
      st.dirHeld[d] = false;
      this.opts.onEvent(st.index, DIR_BUTTON[d], 'up', 0);
    }

    // Presses, dominant axis first (see SECTOR_DIRS), then any d-pad extras.
    if (sector >= 0) {
      const dirs = SECTOR_DIRS[sector];
      for (let i = 0; i < dirs.length; i++) this.pressDir(st, dirs[i], wanted, value, now);
    }
    for (let d = 0; d < DIR_COUNT; d++) this.pressDir(st, d, wanted, value, now);

    // Repeats: at most one per poll, rebased on `now` rather than accumulated,
    // so a backgrounded tab (rAF stops, then resumes with a huge delta) does
    // not fire a burst of twenty cursor moves on return.
    for (let d = 0; d < DIR_COUNT; d++) {
      if (!st.dirHeld[d] || now < st.dirNext[d]) continue;
      st.dirNext[d] = now + REPEAT_INTERVAL_MS;
      this.opts.onEvent(st.index, DIR_BUTTON[d], 'repeat', value);
    }
  }

  private pressDir(
    st: PadState,
    dir: number,
    wanted: boolean[],
    value: number,
    now: number,
  ): void {
    if (!wanted[dir] || st.dirHeld[dir]) return;
    st.dirHeld[dir] = true;
    st.dirNext[dir] = now + REPEAT_DELAY_MS;
    this.opts.onEvent(st.index, DIR_BUTTON[dir], 'down', value);
  }

  /** Fill `out` from whichever d-pad source this pad actually has. */
  private readDpad(pad: Gamepad, st: PadState, out: boolean[]): boolean {
    const layout = st.layout;
    switch (layout.dpad) {
      case 'buttons': {
        const base = layout.dpadBase;
        let any = false;
        for (let d = 0; d < DIR_COUNT; d++) {
          // Button order at `base` is up, down, left, right — same as DIR_*.
          if (isButtonPressed(pad, base + d)) {
            out[d] = true;
            any = true;
          }
        }
        return any;
      }
      case 'hat9':
        return decodeHatAxis(readAxis(pad, 9), out);
      case 'hat6':
        return decodeHatAxis(readAxis(pad, 6), out);
      case 'axes67': {
        const x = readAxis(pad, 6);
        const y = readAxis(pad, 7);
        let any = false;
        if (x <= -DPAD_AXIS_THRESHOLD) {
          out[DIR_LEFT] = true;
          any = true;
        } else if (x >= DPAD_AXIS_THRESHOLD) {
          out[DIR_RIGHT] = true;
          any = true;
        }
        if (y <= -DPAD_AXIS_THRESHOLD) {
          out[DIR_UP] = true;
          any = true;
        } else if (y >= DPAD_AXIS_THRESHOLD) {
          out[DIR_DOWN] = true;
          any = true;
        }
        return any;
      }
      case 'none':
      default:
        return false;
    }
  }

  /**
   * Snap the (already deadzoned) left stick to one of the six hex neighbours.
   * Returns the sector index, or -1 when the stick is inside the deadzone.
   */
  private snapStick(st: PadState): number {
    const x = st.axes.lx;
    const y = st.axes.ly;
    const mag = Math.sqrt(x * x + y * y);
    if (mag <= 0) {
      st.sector = -1;
      st.stickMag = 0;
      return -1;
    }
    st.stickMag = clamp01(mag);

    const angle = Math.atan2(y, x);
    let sector = Math.round(angle / SECTOR_RAD);

    // A pointy-top hex has no neighbour straight up or down, so a perfectly
    // vertical push is exactly equidistant from two of them. Resolve both
    // towards the +x side (up → NE, down → SE) so the two feel symmetric
    // instead of relying on Math.round's asymmetric half-up behaviour.
    const fromVertical = Math.abs(Math.abs(angle) - Math.PI / 2);
    if (fromVertical < VERTICAL_TIE_EPS) sector = angle < 0 ? 5 : 1;

    sector = ((sector % 6) + 6) % 6;

    // Angular hysteresis: stay in the previous sector until the stick is
    // clearly inside the new one.
    const prev = st.sector;
    if (prev >= 0 && prev !== sector) {
      const d = Math.abs(angleDelta(angle, prev * SECTOR_RAD));
      if (d < SECTOR_RAD / 2 + SECTOR_HYSTERESIS_RAD) sector = prev;
    }

    st.sector = sector;
    return sector;
  }
}

/* --------------------------------------------------------------- factories */

function createPadState(pad: Gamepad): PadState {
  const buttons: boolean[] = new Array(Math.max(BUTTON_SLOTS, pad.buttons.length)).fill(false);
  return {
    present: false,
    seen: false,
    index: pad.index,
    id: pad.id,
    label: labelForGamepadId(pad.id, pad.index),
    xbox: isXboxPadId(pad.id),
    layout: resolveLayout(pad),
    buttons,
    trigDown: [false, false],
    trigFullRange: [false, false],
    dirHeld: [false, false, false, false],
    dirNext: [0, 0, 0, 0],
    sector: -1,
    stickMag: 0,
    axes: { lx: 0, ly: 0, rx: 0, ry: 0, lt: 0, rt: 0 },
  };
}

function resetPadState(st: PadState): void {
  for (let i = 0; i < st.buttons.length; i++) st.buttons[i] = false;
  st.trigDown[0] = false;
  st.trigDown[1] = false;
  st.trigFullRange[0] = false;
  st.trigFullRange[1] = false;
  for (let d = 0; d < DIR_COUNT; d++) {
    st.dirHeld[d] = false;
    st.dirNext[d] = 0;
  }
  st.sector = -1;
  st.stickMag = 0;
  zeroAxes(st.axes);
}

/** Attach a no-op rejection handler if the value happens to be a promise. */
function swallow(value: unknown): void {
  if (value && typeof (value as { catch?: unknown }).catch === 'function') {
    (value as Promise<unknown>).catch(() => {});
  }
}
